// WALLET-P1 §6.2: Operator › Billing › Margin — a month's charged · cost · margin per company,
// each expandable into its agents and tasks; CSV export of what is shown (client-side).
import {
  Button,
  EmptyState,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, TrendingUpIcon } from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../../api/client";
import { dollars, microsToMinor } from "../../billing/billing-money";
import { type WalletMargin, walletMarginQuery } from "../../billing/billing-queries";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE } from "../../settings/row-actions";

type Figures = { charged_micros: number; cost_micros: number; margin_micros: number };

/** "12.34" from micros (cents, integer math) for the CSV. */
function csvDollars(micros: number): string {
  const minor = microsToMinor(micros);
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  return `${sign}${(abs - (abs % 100)) / 100}.${String(abs % 100).padStart(2, "0")}`;
}

/** A text cell: a leading `= + - @ tab CR` gets a `'` (no spreadsheet formula), then CSV quoting. */
export const cell = (raw: string) => {
  const s = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The month as CSV: one line per company, then its agents and tasks. */
export function marginCsv(data: WalletMargin): string {
  const lines = [["month", "company", "level", "name", "charged", "cost", "margin"].join(",")];
  const line = (company: string, level: string, name: string, f: Figures) =>
    lines.push(
      [
        data.month,
        cell(company),
        level,
        cell(name),
        csvDollars(f.charged_micros),
        csvDollars(f.cost_micros),
        csvDollars(f.margin_micros),
      ].join(","),
    );
  for (const c of data.companies) {
    line(c.company.name, "company", c.company.name, c);
    for (const a of c.agents) line(c.company.name, "agent", a.label, a);
    for (const t of c.tasks) line(c.company.name, "task", t.label, t);
  }
  return `${lines.join("\n")}\n`;
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Money({ f }: { f: Figures }) {
  return (
    <>
      <TableCell className="text-right tabular-nums">{dollars(f.charged_micros)}</TableCell>
      <TableCell className="text-right tabular-nums">{dollars(f.cost_micros)}</TableCell>
      <TableCell className="text-right tabular-nums">{dollars(f.margin_micros)}</TableCell>
    </>
  );
}

export function Margin({ api, month }: { api: Api; month: string }) {
  const margin = useQuery(walletMarginQuery(api, month));
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="billing-margin">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Margin</h2>
        <Button
          size="sm"
          variant="secondary"
          disabled={!margin.data || margin.data.companies.length === 0}
          onClick={() => margin.data && download(`margin-${month}.csv`, marginCsv(margin.data))}
        >
          <DownloadIcon data-icon="inline-start" aria-hidden="true" />
          Export CSV
        </Button>
      </div>
      {margin.isPending ? (
        <Loading label="Loading margin" rows={3} />
      ) : margin.isError ? (
        <LoadError what="Margin" error={margin.error} />
      ) : margin.data.companies.length === 0 ? (
        <EmptyState variant="card" icon={TrendingUpIcon} title="No companies granted" />
      ) : (
        <>
          {margin.data.complete ? null : (
            <Notice tone="warning" title="Incomplete month">
              {margin.data.incomplete_reasons.join(" ")}
            </Notice>
          )}
          <Table className={DENSE_TABLE} data-slot="margin-table">
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Company</TableHead>
                <TableHead scope="col" className="text-right">
                  Charged
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Cost
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Margin
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {margin.data.companies.map((c) => {
                const expanded = open.has(c.company.id);
                const expandable = c.agents.length + c.tasks.length > 0;
                return (
                  <Fragment key={c.company.id}>
                    <TableRow data-margin={c.company.slug}>
                      <TableHead scope="row" className="font-normal">
                        {expandable ? (
                          <button
                            type="button"
                            aria-expanded={expanded}
                            className="inline-flex items-center gap-1 hover:underline"
                            onClick={() => toggle(c.company.id)}
                          >
                            {expanded ? (
                              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
                            ) : (
                              <ChevronRightIcon aria-hidden="true" className="size-3.5" />
                            )}
                            {c.company.name}
                          </button>
                        ) : (
                          <span className="pl-[18px]">{c.company.name}</span>
                        )}
                      </TableHead>
                      <Money f={c} />
                    </TableRow>
                    {expanded
                      ? (
                          [
                            ["Agent", c.agents],
                            ["Task", c.tasks],
                          ] as const
                        ).flatMap(([kind, list]) =>
                          list.map((l) => (
                            <TableRow
                              key={`${kind}:${l.key}`}
                              data-margin-line={kind.toLowerCase()}
                              className="text-muted-foreground"
                            >
                              <TableHead scope="row" className="pl-8 font-normal">
                                <span className="text-xs">{kind}</span> · {l.label}
                              </TableHead>
                              <Money f={l} />
                            </TableRow>
                          )),
                        )
                      : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </section>
  );
}
